import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Zap, Users, Building2, Rocket, ArrowRight, Shield, Clock, CreditCard, Construction } from 'lucide-react';
import NotifyMeForm from '../components/NotifyMeForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: 'business',
    name: 'Business Core',
    tagline: 'For small teams getting started',
    price: 29,
    description: 'Everything you need to run a small business end-to-end',
    popular: false,
    employeesCap: 10,
    clientsCap: 100,
    usersCap: 3,
    coreModules: [
      'Email · Calendar · Documents',
      'Quotes · Invoices · Accounts Receivable',
      'Clients (up to 100) · Services & Goods',
      'Employees (up to 10) · Onboarding · Training',
      'Performance · Benefits · Pensions · ROEs',
      'Internal Payroll tracking & summaries',
      'Equipment · Fleet · Safety · Policies · Certifications',
      'Accounts Payable · Banking · Tax & Compliance · Reporting',
      'Portal Management · 3 admin users',
    ],
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
  },
  {
    id: 'enterprise',
    name: 'Enterprise Core',
    tagline: 'Built for larger teams ready to streamline with ease',
    price: 49,
    description: 'Built for larger teams ready to streamline with ease',
    popular: true,
    employeesCap: 25,
    clientsCap: 500,
    usersCap: 10,
    coreModules: [
      'Everything in Business Core',
      'Clients (up to 500) · Employees (up to 25)',
      'Advanced Analytics · Audit Logs',
      'Automated Payroll workflows (internal)',
      'Priority Support · 10 admin users',
    ],
    icon: Users,
    color: 'from-violet-500 to-purple-500',
  },
  {
    id: 'corporate',
    name: 'Corporate Core',
    tagline: 'For large organizations',
    price: null,
    priceLabel: 'Custom',
    description: 'Unlimited everything · multi-entity · custom integrations',
    popular: false,
    underConstruction: true,
    employeesCap: 'Unlimited',
    clientsCap: 'Unlimited',
    usersCap: 'Unlimited',
    coreModules: [
      'Everything in Enterprise Core',
      'Unions Management',
      'Unlimited Employees & Clients',
      'Multi-entity Consolidation · SSO',
      'Dedicated Account Manager · SLA',
    ],
    icon: Building2,
    color: 'from-amber-500 to-orange-500',
  },
];

// Phase 17: Optional add-on modules. These are SEPARATE from the Core packages above —
// they add **new capability** on top of any Core plan, billed separately. All four are
// currently Under Construction; users can request early access via NotifyMeForm.
const ADDON_MODULES_CATALOG = [
  {
    id: 'payroll_live',
    name: 'LIVE Payroll Processing',
    description: 'Run real CRA-compliant payroll: direct deposits, T4s, source deductions remittance.',
  },
  {
    id: 'ap_live',
    name: 'LIVE Accounts Payable',
    description: 'Pay vendors directly from the app, bank feeds and bill capture included.',
  },
  {
    id: 'ar_live',
    name: 'LIVE Accounts Receivable',
    description: 'Auto-charge cards, ACH debits, real-time reconciliation with your bank.',
  },
  {
    id: 'pos_live',
    name: 'LIVE POS',
    description: 'Accept card-present payments with hardware integrations and real-time inventory.',
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  // If user is already logged in, redirect to manage subscription
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/manage-subscription');
    }
  }, [isAuthenticated, user, navigate]);

  const handleSelectPlan = (planId) => {
    navigate(`/signup?plan=${planId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/business-genie-logo.png"
              alt="Business Genie"
              className="h-10 w-10 rounded-xl object-contain bg-white/5"
              data-testid="pricing-bg-logo"
            />
            <span className="text-xl font-bold text-white">Business Genie</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-white/70 hover:text-white" onClick={() => navigate('/login')}>
              Sign In
            </Button>
            <Button className="bg-white text-slate-900 hover:bg-white/90" onClick={() => navigate('/signup')}>
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 pt-20 pb-16 text-center">
        <Badge variant="outline" className="mb-4 border-cyan-500/50 text-cyan-400">
          Simple, transparent pricing
        </Badge>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
          Choose the plan that fits
          <span className="block bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            your business
          </span>
        </h1>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
          Start with a 30-day free trial. No credit card required.
          Cancel anytime.
        </p>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <span className={`text-sm ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-500'}`}>
            Monthly
          </span>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              billingCycle === 'annual' ? 'bg-cyan-500' : 'bg-slate-600'
            }`}
            data-testid="billing-cycle-toggle"
          >
            <span
              className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                billingCycle === 'annual' ? 'translate-x-8' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-sm ${billingCycle === 'annual' ? 'text-white' : 'text-slate-500'}`}>
            Yearly
            <Badge variant="secondary" className="ml-2 bg-green-500/20 text-green-400 text-xs">
              Save 15%
            </Badge>
          </span>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="container mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            // Phase 12: yearly = 15% off monthly (matches backend price_year computation)
            const monthlyPrice = plan.price;
            const yearlyMonthlyEquiv = monthlyPrice === null ? null : Math.round(monthlyPrice * 0.85 * 100) / 100;
            const displayedPrice = monthlyPrice === null ? null : (billingCycle === 'annual' ? yearlyMonthlyEquiv : monthlyPrice);
            
            return (
              <Card
                key={plan.id}
                className={`relative bg-slate-800/50 border-slate-700 backdrop-blur-sm overflow-hidden ${
                  plan.popular ? 'ring-2 ring-cyan-500 scale-105' : ''
                } ${plan.underConstruction ? 'opacity-90' : ''}`}
                data-testid={`pricing-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                    MOST POPULAR
                  </div>
                )}
                {plan.underConstruction && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                    UNDER CONSTRUCTION
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-white">{plan.name}</CardTitle>
                  <CardDescription className="text-slate-400">
                    {plan.tagline || plan.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="pb-6">
                  <div className="flex items-baseline gap-1 mb-2">
                    {displayedPrice === null ? (
                      <span className="text-3xl font-bold text-white">{plan.priceLabel || 'Custom'}</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-white">${displayedPrice}</span>
                        <span className="text-slate-400">/month</span>
                      </>
                    )}
                  </div>
                  {billingCycle === 'annual' && monthlyPrice !== null && (
                    <p className="text-xs text-green-400 mb-4">
                      Billed yearly · ${(yearlyMonthlyEquiv * 12).toFixed(2)}/yr
                    </p>
                  )}
                  {billingCycle === 'monthly' && monthlyPrice !== null && (
                    <p className="text-xs text-slate-500 mb-4">Billed monthly</p>
                  )}
                  {displayedPrice === null && (
                    <p className="text-xs text-amber-400 mb-4">Contact our team for custom pricing</p>
                  )}

                  {/* Plan caps */}
                  <div className="grid grid-cols-3 gap-2 mb-6 p-3 rounded-lg bg-slate-900/50 border border-slate-700">
                    <div className="text-center">
                      <p className="text-xs text-slate-500">Employees</p>
                      <p className="text-sm font-bold text-white">{plan.employeesCap}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500">Clients</p>
                      <p className="text-sm font-bold text-white">{plan.clientsCap}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500">Admin Users</p>
                      <p className="text-sm font-bold text-white">{plan.usersCap}</p>
                    </div>
                  </div>

                  {/* Core modules — what comes WITH this Core package */}
                  <p className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-2">
                    Core Modules Included
                  </p>
                  <ul className="space-y-2 mb-2">
                    {plan.coreModules.map((m, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-300">{m}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Corporate plan — capture interest directly */}
                  {plan.underConstruction && (
                    <div className="mt-4 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                      <p className="text-xs text-slate-300 mb-1.5">
                        Want early access? We'll email you when Corporate launches.
                      </p>
                      <NotifyMeForm
                        featureKey="corporate"
                        planId="corporate"
                        dataTestId="notify-corporate"
                      />
                    </div>
                  )}
                </CardContent>
                
                <CardFooter>
                  <Button
                    className={`w-full ${
                      plan.underConstruction
                        ? 'bg-slate-700/50 text-slate-400 cursor-not-allowed'
                        : plan.popular
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white'
                          : 'bg-slate-700 hover:bg-slate-600 text-white'
                    }`}
                    onClick={() => !plan.underConstruction && handleSelectPlan(plan.id)}
                    disabled={loading === plan.id || plan.underConstruction}
                    data-testid={`select-plan-${plan.id}-btn`}
                  >
                    {plan.underConstruction ? 'Contact Sales' : (loading === plan.id ? 'Loading...' : 'Start Free Trial')}
                    {!plan.underConstruction && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Phase 17: Optional Add-on Modules (separate from Core packages) */}
      <section className="container mx-auto px-4 pb-20" data-testid="addon-modules-section">
        <div className="text-center mb-10">
          <Badge variant="outline" className="border-amber-500/30 text-amber-300 bg-amber-500/10 mb-3">
            Optional Add-ons · sold separately
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Add-on Modules
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            These modules extend any Core package with extra capability and are billed in addition to your Core subscription.
            All add-ons below are currently under active development — sign up to be notified the moment each one launches.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {ADDON_MODULES_CATALOG.map((a) => (
            <Card
              key={a.id}
              className="bg-slate-800/40 border-slate-700 backdrop-blur-sm relative overflow-hidden"
              data-testid={`addon-card-${a.id}`}
            >
              <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">
                UNDER CONSTRUCTION
              </div>
              <CardHeader className="pb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-3">
                  <Construction className="h-5 w-5 text-amber-400" />
                </div>
                <CardTitle className="text-lg text-white">{a.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-slate-400 leading-relaxed min-h-[60px]">{a.description}</p>
                <NotifyMeForm
                  featureKey={a.id}
                  planId="addon"
                  dataTestId={`notify-${a.id}`}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Trust Badges */}
      <section className="container mx-auto px-4 pb-20">
        <div className="flex flex-wrap items-center justify-center gap-8 text-slate-400">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-400" />
            <span>SSL Secured</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-cyan-400" />
            <span>30-Day Free Trial</span>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-violet-400" />
            <span>No Credit Card Required</span>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="container mx-auto px-4 pb-20">
        <h2 className="text-2xl font-bold text-white text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="max-w-2xl mx-auto space-y-4">
          {[
            {
              q: 'Do I need a credit card to start?',
              a: "No. We do NOT require a credit card to start your 30-day free trial — sign up and start using Business Genie immediately. You'll only be prompted to add a payment method when your trial ends if you want to continue.",
            },
            {
              q: 'Can I switch plans later?',
              a: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately with prorated billing.',
            },
            {
              q: 'What happens after my trial ends?',
              a: "We'll email you a reminder a few days before your 30-day trial ends. When the trial expires you'll be prompted to add a credit card to keep working. If you choose not to subscribe, your account is paused but your data is retained for 90 days in case you change your mind.",
            },
            {
              q: 'Can I add my credit card before the trial ends?',
              a: "Yes — at any time during your trial you can open Account & Subscription in your portal and add a payment method to convert your account to an active subscription. Adding a card before trial-end ensures uninterrupted access.",
            },
            {
              q: 'Is there a setup fee?',
              a: 'No setup fees ever. Just pick a plan and start using Business Genie immediately.',
            },
            {
              q: 'Can I cancel anytime?',
              a: "Absolutely. Cancel with one click from your dashboard — during the trial or after. You'll retain access until the end of your current billing period and we'll never charge you again.",
            },
          ].map((faq, idx) => (
            <div key={idx} className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
              <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
              <p className="text-slate-400">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          © 2026 Business Genie. All rights reserved.
          <div className="mt-2 flex flex-wrap items-center justify-center gap-4">
            <a href="/legal/terms" className="hover:text-cyan-400 transition-colors" data-testid="footer-terms-link">Terms of Service</a>
            <a href="/legal/privacy" className="hover:text-cyan-400 transition-colors" data-testid="footer-privacy-link">Privacy Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
